import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { Table } from 'antd-v5';
import { ROUTER_MY_NETWORK_ORDERS_TAB } from 'app/AppRouter';
import Loader from 'components/Loader';
import { useFetchMyClientsOrderDetail } from 'domains/order/Order.requests';
import {
  getEventsColumns,
  getLinesColumns,
} from 'pages/MyOrdersPage/ClientsOrders/ClientOrderDetail/ClientOrderDetailPage.utils';
import OrderHeader from 'pages/MyOrdersPage/ClientsOrders/ClientOrderDetail/OrderHeader';
import { BlackButton, Flex, Text } from 'UI';
import { getSearchData, isError, isLoading, isNotFound } from 'utils';

const ClientOrderDetailPage = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const { t } = useTranslation();
  const history = useHistory();
  const selectedOrder = useFetchMyClientsOrderDetail(orderId);
  const orderData = getSearchData(selectedOrder)?.order;

  const handleBack = () => {
    history.push(ROUTER_MY_NETWORK_ORDERS_TAB);
  };

  if (!selectedOrder?.searchStatus || isLoading(selectedOrder?.searchStatus)) {
    return <Loader />;
  }

  if (!orderData || isError(selectedOrder?.searchStatus) || isNotFound(selectedOrder?.searchStatus)) {
    return (
      <Flex
        direction={'column'}
        align={'center'}
        justify={'center'}
        minHeight={'100%'}
        gap={20}
        data-cy="{order-not-found}"
      >
        <Text type={'h1_banner_light'}>{t('clients_order.detail.error', 'Order not found')}</Text>
        <BlackButton onClick={handleBack} size={'large'}>
          {t('common.back', 'Back')}
        </BlackButton>
      </Flex>
    );
  }

  const linesColumns = getLinesColumns(t, orderData.currency);
  const eventsColumns = getEventsColumns(t);

  return (
    <Flex direction={'column'} padding={'15px 30px 100px 30px'}>
      <Text type={'h1_banner_light'} data-cy={'order-id'}>
        {t('clients_order.detail.page_title', 'Order n°: {{orderNumber}}', { orderNumber: orderData.orderNumber })}
      </Text>
      <Flex direction={'column'} gap={20}>
        <OrderHeader order={orderData} />
        <Table
          bordered
          columns={linesColumns}
          dataSource={orderData.lines ?? []}
          rowKey={(row) => `${row.reference}-${row.supplierName}`}
          pagination={false}
          data-cy={'references-table'}
          onRow={(row) => ({ 'data-cy': `reference-row-${row.reference}` } as React.HTMLAttributes<HTMLElement>)}
        />
        <Flex direction={'column'} gap={12}>
          <Text type="heading4">{t('clients_order.detail.events_title', 'Events linked to your order')}</Text>
          <Table
            bordered
            columns={eventsColumns}
            dataSource={orderData.events ?? []}
            rowKey={(row, idx) => `${row.eventType}-${idx}`}
            pagination={false}
            data-cy={'events-linked-to-order-table'}
            onRow={(row) => ({ 'data-cy': `event-type-result-${row.eventType}` } as React.HTMLAttributes<HTMLElement>)}
          />
        </Flex>
      </Flex>
    </Flex>
  );
};

export default ClientOrderDetailPage;
